import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import { RootContext } from "../../../context/RootContext";
import { LoansContext } from "../../../context/LoansContext";

import { Clients } from "../../../types/enums";

import { useLoanSizingHandler } from "../../../hooks/useLoanSizingHandler";

import { getBorrowerFields } from "../LoanFields/LoanDetails/BorrowerInformation";
import { getPropertyLocationFields } from "../LoanFields/LoanDetails/PropertyLocationFields";
import { getPropertyInformationFields } from "../LoanFields/LoanDetails/PropertyInformationFields";
import { getPropertyEconomicsFields } from "../LoanFields/LoanDetails/PropertyEconomicsFields";
import { getLoanInformationFields } from "../LoanFields/LoanDetails/LoanInformation";
import { getLoanEconomicsFields } from "../LoanFields/LoanDetails/LoanEconomics";
import { getLoanCharacterizationFields } from "../LoanFields/LoanResults/LoanCharacterization";
import { getLoanFeaturesFields } from "../LoanFields/LoanResults/LoanFeatures";
import { getUnitInformationFields } from "../LoanFields/LoanDetails/UnitInformation";

import BorrowerDetails from "./BorrowerDetails";
import LoanDetails from "./LoanDetails";
import PropertyDetails from "./PropertyDetails";
import AutoCalculatedResults from "./AutoCalculatedResults";

const unitInformation: any = [{}];
const propertyDetails = [
  {
    propertyLocation: {},
    propertyInformation: {},
    propertyEconomics: {},
    unitInformation,
  },
];

export function DetailedLoanSizing() {
  const { t } = useTranslation();
  const [client, setClient] = useState<string>("");

  const { dynamicConfig } = useContext(RootContext);
  const {
    handleChange,
    onBlur,
    addressOrPostalOnInputChange,
    borrowerOnInputChange
  } = useLoanSizingHandler();

  const {
    loanDetails,
    selectedSizingType,
    zipCodeOptions,
    addressOptions,
    detailedLoanSizingData,
    setDetailedLoanSizingData,
    fieldConfig,
    setFieldConfig,
    detailedLoanSizingErrors,
    autoCalculatedLoanResults,
    autoCalculatedPropResults,
    setCondoEligibility,
    borrowerOptions
  } = useContext(LoansContext);

  const handleAddProperty = () => {
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: [
        ...detailedLoanSizingData?.propertyDetails,
        ...propertyDetails,
      ],
    });
  };

  const handleAddUnit = (propertyId: number) => {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];
    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails?.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              unitInformation: [
                ...(property?.unitInformation || []),
                ...unitInformation,
              ],
            };
          }
          return property;
        }
      );
    }
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  };

  const handleDeleteProperty = (propertyId: number) => {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];
    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails?.filter(
        (property: any, index: number) => index !== propertyId
      );
    }
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  };

  const handleDeleteUnit = (propertyId: number, unitId: number) => {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];
    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails?.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              unitInformation: property?.unitInformation?.filter(
                (unit: any, idx: number) => idx !== unitId
              ),
            };
          }
          return property;
        }
      );
    }
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  };

  // Initialize field config
  useEffect(() => {
    const client =
      dynamicConfig.ViteAppKcRealm === "bplsizer"
        ? Clients?.BPL_SIZER
        : Clients?.MMTC;

    let sizingTypeForFields = loanDetails?.productType === "Bridge Loan" && client === "mmtc" ?  loanDetails?.propertyType : selectedSizingType; 
    setClient(client);
    setFieldConfig({
      borrowerDetails: {
        borrowerInformation: {
          fields: getBorrowerFields(
          loanDetails?.productType,
          client,
          "borrowerInformation",
          sizingTypeForFields
        ),
        header: "borrowerInformation",
        },
      },
      propertyDetails: {
        propertyLocation: {
          fields: getPropertyLocationFields(
            loanDetails?.productType,
            client,
            "propertyLocation",
            sizingTypeForFields
          ),
          header: "propertyLocation",
        },
        propertyInformation: {
          fields: getPropertyInformationFields(
            loanDetails?.productType,
            client,
            "propertyInformation",
            sizingTypeForFields
          ),
          header: "propertyInformation",
        },
        unitInformation: {
          fields: getUnitInformationFields(
            loanDetails?.productType,
            client,
            "unitInformation",
            sizingTypeForFields
          ),
          header: "unitInformation",
        },
        propertyEconomics: {
          fields: getPropertyEconomicsFields(
            loanDetails?.productType,
            client,
            "propertyEconomics",
            sizingTypeForFields
          ),
          header: "propertyEconomics",
        },
      },
      loanDetails: {
        loanInformation: {
          fields: getLoanInformationFields(
            loanDetails?.productType,
            client,
            "loanInformation",
            sizingTypeForFields
          ),
          header: "loanInformation",
        },
        loanEconomics: {
          fields: getLoanEconomicsFields(
            loanDetails?.productType,
            client,
            "loanEconomics",
            sizingTypeForFields
          ),
          header: "loanEconomics",
        },
      },
      autoCalculatedResults: {
        loanCharacterisation: {
          fields: getLoanCharacterizationFields(
            loanDetails?.productType,
            client,
            "loanCharacterisation",
            loanDetails?.propertyType,
            sizingTypeForFields
          ),
          header: "loanCharacterisation",
        },
        loanFeatures: {
          fields: getLoanFeaturesFields(
            loanDetails?.productType,
            client,
            "loanFeatures",
            loanDetails?.propertyType,
            sizingTypeForFields,
            false
          ),
          header: "loanFeatures",
        },
        propLoanFeatures: {
            fields: getLoanFeaturesFields(
              loanDetails?.productType,
              client,
              "propLoanFeatures",
              loanDetails?.propertyType,
              sizingTypeForFields,
              true
            ),
            header: "propLoanFeatures",
        },
        marketData: {
          fields: getLoanFeaturesFields(
            loanDetails?.productType,
            client,
            "marketData",
            loanDetails?.propertyType,
            sizingTypeForFields
          ),
          header: "marketData",
        },
        subMarketData: {
          fields: getLoanFeaturesFields(
            loanDetails?.productType,
            client,
            "subMarketData",
            loanDetails?.propertyType,
            sizingTypeForFields
          ),
          header: "subMarketData",
        },
      },
    });
  }, [
    dynamicConfig,
    loanDetails?.productType,
    selectedSizingType,
    setFieldConfig,
  ]);

  return (
    <div style={{ margin: "0px 16px" }}>
      <BorrowerDetails
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        detailedLoanSizingData={detailedLoanSizingData}
        client={client}
        handleChange={handleChange}
        onBlur={onBlur}
        detailedLoanSizingErrors={detailedLoanSizingErrors}
        loanDetails={loanDetails}
        borrowerOnInputChange={borrowerOnInputChange}
        borrowerOptions={borrowerOptions}
      />
      <PropertyDetails
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        detailedLoanSizingData={detailedLoanSizingData}
        detailedLoanSizingErrors={detailedLoanSizingErrors}
        client={client}
        handleChange={handleChange}
        onBlur={onBlur}
        loanDetails={loanDetails}
        handleAddUnit={handleAddUnit}
        handleAddProperty={handleAddProperty}
        handleDeleteUnit={handleDeleteUnit}
        handleDeleteProperty={handleDeleteProperty}
        zipCodeOptions={zipCodeOptions}
        addressOptions={addressOptions}
        addressOrPostalOnInputChange={addressOrPostalOnInputChange}
        allowEditAfterSubmit={dynamicConfig?.allowEditAfterSubmit}
        setDetailedLoanSizingData={setDetailedLoanSizingData}
      />
      <LoanDetails
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        detailedLoanSizingData={detailedLoanSizingData}
        client={client}
        detailedLoanSizingErrors={detailedLoanSizingErrors}
        handleChange={handleChange}
        onBlur={onBlur}
        loanDetails={loanDetails}
        setCondoEligibility={setCondoEligibility}
        setDetailedLoanSizingData={setDetailedLoanSizingData}
        results={{ autoCalculatedLoanResults, autoCalculatedPropResults }}
      />
      <AutoCalculatedResults
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        data={{ autoCalculatedLoanResults, autoCalculatedPropResults }}
        client={client}
        loanDetails={loanDetails}
        t={t}
        handleChange={handleChange}
        onBlur={onBlur}
      />
    </div>
  );
}
